import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./Events.css";
import { useTranslation } from "react-i18next";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import BannerComponent from "../../components/bannerComponent/bannerComponent";
import EventCardsComponent from "../../components/EventCards/EventCardsComponent";
import ButtonLinkComponent from "../../components/buttonLink/buttonLink.jsx";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

const EVENTS_API =
  "https://prod-94.westus.logic.azure.com:443/workflows/45d529b349d3458c98ff7445314f802d/triggers/manual/paths/invoke?api-version=2016-06-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=LJ4GKyIWBEy3EyV9q8AAirIEtkqRFBIe5MfMr4eMEbk";

const Events = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    setIsLoading(true);

    axios
      .get(EVENTS_API)
      .then((response) => {
        setEvents(Array.isArray(response.data) ? response.data : []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      });
  }, []);

  const liveEvents = useMemo(
    () => events.filter((event) => event.CATEGORY === 3),
    [events]
  );

  const upcomingEvents = useMemo(
    () => events.filter((event) => event.CATEGORY === 1),
    [events]
  );

  const recordedEvents = useMemo(
    () => events.filter((event) => event.CATEGORY === 2),
    [events]
  );

  const featuredEvents = useMemo(() => {
    if (liveEvents.length > 0) return liveEvents.slice(0, 2);
    if (upcomingEvents.length > 0) return upcomingEvents.slice(0, 2);
    if (recordedEvents.length > 0) return recordedEvents.slice(0, 2);
    return [];
  }, [liveEvents, upcomingEvents, recordedEvents]);

  function openBooking() {
    const BOOKING_URL =
      "https://outlook.office365.com/book/G07e86683359a41f58322cab8b2047723@5igsolutions.com/?ismsaljsauthenabled=true&RefID=EVENTS";

    window.open(BOOKING_URL, "_blank", "noopener,noreferrer");
  }

  function EmptyState({ title, text }) {
    return (
      <div className="eventsEmptyState">
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    );
  }

  return (
    <>
      <Header />

      <main className="eventsPage">
        <section className="eventsHero eventsHeroFull">
          <div className="eventsHeroBannerFull">
            <BannerComponent
              translateTitle={t("EVENTS_HERO_TITLE")}
              translateText={t("EVENTS_HERO_SUBTITLE")}
              className="eventsHeroBanner"
              classNameSide="l"
              translateButton={t("EVENTS_HERO_CTA")}
              chipText={t("HEADER_EVENTS")}
              onClick={openBooking}
              headingTag="h1"
            />
          </div>
        </section>

        {isLoading ? (
          <div className="eventsLoadingContainer">
            <Box sx={{ display: "flex" }}>
              <CircularProgress />
            </Box>
          </div>
        ) : (
          <>
            <section className="eventsIntro">
              <div className="eventsSectionIntro">
                <h2>{t("EVENTS_INTRO_TITLE")}</h2>
                <p>{t("EVENTS_INTRO_SUBTITLE")}</p>
              </div>
            </section>

            <section className="eventsHighlights">
              <div className="eventsBlockWrap">
                <div className="eventsSectionIntro">
                  <h2>{t("EVENTS_FEATURED_TITLE")}</h2>
                  <p>{t("EVENTS_FEATURED_SUBTITLE")}</p>
                </div>

                {featuredEvents.length > 0 ? (
                  <EventCardsComponent cardsData={featuredEvents} />
                ) : (
                  <EmptyState
                    title={t("EVENTS_EMPTY_FEATURED_TITLE")}
                    text={t("EVENTS_EMPTY_FEATURED_DESC")}
                  />
                )}
              </div>
            </section>

            <section className="eventsValue">
              <div className="eventsBlockWrap">
                <div className="eventsSectionIntro">
                  <h2>{t("EVENTS_VALUE_TITLE")}</h2>
                  <p>{t("EVENTS_VALUE_SUBTITLE")}</p>
                </div>

                <div className="eventsValueGrid">
                  <article className="eventsValueCard">
                    <h3>{t("EVENTS_VALUE_CARD_1_TITLE")}</h3>
                    <p>{t("EVENTS_VALUE_CARD_1_DESC")}</p>
                  </article>

                  <article className="eventsValueCard">
                    <h3>{t("EVENTS_VALUE_CARD_2_TITLE")}</h3>
                    <p>{t("EVENTS_VALUE_CARD_2_DESC")}</p>
                  </article>

                  <article className="eventsValueCard">
                    <h3>{t("EVENTS_VALUE_CARD_3_TITLE")}</h3>
                    <p>{t("EVENTS_VALUE_CARD_3_DESC")}</p>
                  </article>

                  <article className="eventsValueCard">
                    <h3>{t("EVENTS_VALUE_CARD_4_TITLE")}</h3>
                    <p>{t("EVENTS_VALUE_CARD_4_DESC")}</p>
                  </article>
                </div>
              </div>
            </section>

            <section className="eventsListing">
              <div className="eventsEventsSection">
                <div className="eventsSectionHeader">
                  <div>
                    <h2>{t("LIVE_EVENTS")}</h2>
                    <p>{t("EVENTS_LIVE_SUBTITLE")}</p>
                  </div>
                  <span className="eventsCount">{liveEvents.length}</span>
                </div>

                {liveEvents.length > 0 ? (
                  <EventCardsComponent cardsData={liveEvents} />
                ) : (
                  <EmptyState
                    title={t("EVENTS_EMPTY_LIVE_TITLE")}
                    text={t("EVENTS_EMPTY_LIVE_DESC")}
                  />
                )}
              </div>

              <div className="eventsEventsSection">
                <div className="eventsSectionHeader">
                  <div>
                    <h2>{t("UP_COMING_EVENTS")}</h2>
                    <p>{t("EVENTS_UPCOMING_SUBTITLE")}</p>
                  </div>
                  <span className="eventsCount">{upcomingEvents.length}</span>
                </div>

                {upcomingEvents.length > 0 ? (
                  <EventCardsComponent cardsData={upcomingEvents} />
                ) : (
                  <EmptyState
                    title={t("EVENTS_EMPTY_UPCOMING_TITLE")}
                    text={t("EVENTS_EMPTY_UPCOMING_DESC")}
                  />
                )}
              </div>

              <div className="eventsEventsSection">
                <div className="eventsSectionHeader">
                  <div>
                    <h2>{t("RECORDED_EVENTS")}</h2>
                    <p>{t("EVENTS_RECORDED_SUBTITLE")}</p>
                  </div>
                  <span className="eventsCount">{recordedEvents.length}</span>
                </div>

                {recordedEvents.length > 0 ? (
                  <EventCardsComponent cardsData={recordedEvents} />
                ) : (
                  <EmptyState
                    title={t("EVENTS_EMPTY_RECORDED_TITLE")}
                    text={t("EVENTS_EMPTY_RECORDED_DESC")}
                  />
                )}
              </div>
            </section>

            <section className="eventsFaq">
              <div className="eventsBlockWrap">
                <div className="eventsSectionIntro">
                  <h2>{t("EVENTS_FAQ_TITLE")}</h2>
                </div>

                <div className="eventsFaqGrid">
                  <article className="eventsFaqCard">
                    <h3>{t("EVENTS_FAQ_1_TITLE")}</h3>
                    <p>{t("EVENTS_FAQ_1_DESC")}</p>
                  </article>

                  <article className="eventsFaqCard">
                    <h3>{t("EVENTS_FAQ_2_TITLE")}</h3>
                    <p>{t("EVENTS_FAQ_2_DESC")}</p>
                  </article>

                  <article className="eventsFaqCard">
                    <h3>{t("EVENTS_FAQ_3_TITLE")}</h3>
                    <p>{t("EVENTS_FAQ_3_DESC")}</p>
                  </article>
                </div>
              </div>
            </section>

            <section className="eventsCta">
              <div className="eventsCtaInner">
                <div className="eventsCtaContent">
                  <h2>{t("EVENTS_CTA_TITLE")}</h2>
                  <p>{t("EVENTS_CTA_DESC")}</p>
                </div>

                <ButtonLinkComponent
                  translateButton={t("EVENTS_CTA_BUTTON")}
                  url="https://outlook.office365.com/book/G07e86683359a41f58322cab8b2047723@5igsolutions.com/?RefID=EVENTS"
                  blank={true}
                />
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
    </>
  );
};

export default Events;